"use client";

import { useCallback, useMemo } from "react";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type ExecutiveOrderLabels,
  executiveOrderIsReady,
  EXECUTIVE_ORDER_FIELDS as F,
  formatExecutiveOrder,
} from "@/lib/tool-kit/legal-sources/executive-order-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const EXECUTIVE_ORDER_CITATION_GENERATOR_STRINGS = {
  formHeading: "Executive order details",
  resultsHeading: "Your citations",
  emptyPrompt:
    "Enter the order number and either its Federal Register volume and page or its 3 C.F.R. page and year to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  numberLabel: "Executive order number",
  numberPlaceholder: "13676",
  titleLabel: "Title of the order",
  titlePlaceholder: "Combating Antibiotic-Resistant Bacteria",
  presidentLabel: "President who signed it",
  presidentPlaceholder: "Barack Obama",
  signedLabel: "Date signed",
  signedPlaceholder: "YYYY-MM-DD",
  frVolumeLabel: "Federal Register volume",
  frVolumePlaceholder: "79",
  frIssueLabel: "Federal Register issue number (for MLA)",
  frIssuePlaceholder: "184",
  frPageLabel: "First page in the Federal Register",
  frPagePlaceholder: "56931",
  frLastPageLabel: "Last page (for MLA)",
  frLastPagePlaceholder: "56935",
  frDateLabel: "Federal Register publication date",
  frDatePlaceholder: "YYYY-MM-DD",
  frDateHint: "The date of that day's Federal Register, not the signing date.",
  cfrPageLabel: "Page in 3 C.F.R. (optional)",
  cfrPagePlaceholder: "294",
  cfrPageHint:
    "Orders are later compiled in title 3 of the Code of Federal Regulations. APA cites that page when it exists.",
  cfrYearLabel: "Year of the 3 C.F.R. compilation",
  cfrYearPlaceholder: "2014",
  urlLabel: "URL of the copy you read",
  urlPlaceholder:
    "https://www.govinfo.gov/content/pkg/FR-2014-09-23/pdf/2014-22805.pdf",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  apaNote:
    "APA 7 (§11.7) cites the order where it is compiled in title 3 of the C.F.R.; until then it uses the Federal Register, which APA's manual abbreviates F.R. Five-digit order numbers take a comma, as in legal citation.",
  mlaNote:
    "MLA cites the copy you read — here the Federal Register printing — with the president in square brackets and no comma in the order number.",
  chicagoNote:
    "Chicago follows the Bluebook and dates the Federal Register printing, not the signature. Notes only; law reviews abbreviate the month (Sept., not September).",
  example: {
    number: "13676",
    title: "Combating Antibiotic-Resistant Bacteria",
    president: "Barack Obama",
    signed: "2014-09-18",
    frVolume: "79",
    frIssue: "184",
    frPage: "56931",
    frLastPage: "56935",
    frDate: "2014-09-23",
    cfrPage: "294",
    cfrYear: "2014",
    url: "https://www.govinfo.gov/content/pkg/FR-2014-09-23/pdf/2014-22805.pdf",
  },
} as const;

type Strings = typeof EXECUTIVE_ORDER_CITATION_GENERATOR_STRINGS;

function buildFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.number,
      kind: "text",
      label: strings.numberLabel,
      placeholder: strings.numberPlaceholder,
    },
    {
      id: F.president,
      kind: "text",
      label: strings.presidentLabel,
      placeholder: strings.presidentPlaceholder,
    },
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      wide: true,
    },
    {
      id: F.signed,
      kind: "date",
      label: strings.signedLabel,
      placeholder: strings.signedPlaceholder,
    },
    {
      id: F.frDate,
      kind: "date",
      label: strings.frDateLabel,
      placeholder: strings.frDatePlaceholder,
      hint: strings.frDateHint,
    },
    {
      id: F.frVolume,
      kind: "text",
      label: strings.frVolumeLabel,
      placeholder: strings.frVolumePlaceholder,
    },
    {
      id: F.frIssue,
      kind: "text",
      label: strings.frIssueLabel,
      placeholder: strings.frIssuePlaceholder,
    },
    {
      id: F.frPage,
      kind: "text",
      label: strings.frPageLabel,
      placeholder: strings.frPagePlaceholder,
    },
    {
      id: F.frLastPage,
      kind: "text",
      label: strings.frLastPageLabel,
      placeholder: strings.frLastPagePlaceholder,
    },
    {
      id: F.cfrPage,
      kind: "text",
      label: strings.cfrPageLabel,
      placeholder: strings.cfrPagePlaceholder,
      hint: strings.cfrPageHint,
    },
    {
      id: F.cfrYear,
      kind: "text",
      label: strings.cfrYearLabel,
      placeholder: strings.cfrYearPlaceholder,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
      wide: true,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function executiveOrderLabels(strings: Strings): ExecutiveOrderLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function ExecutiveOrderCitationGeneratorWidget() {
  const strings = useToolUi(EXECUTIVE_ORDER_CITATION_GENERATOR_STRINGS);
  const fields = useMemo(() => buildFields(strings), [strings]);
  const labels = useMemo(() => executiveOrderLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatExecutiveOrder(values, labels),
    [labels]
  );

  return (
    <SourceCitationBuilder
      example={strings.example}
      fields={fields}
      format={format}
      isReady={executiveOrderIsReady}
      strings={strings}
    />
  );
}
